/**
 **
 * This is a dictionary of name to data mapping functions for holding analytics columns
 * This is extremely bespoke because we do not have a methodical naming system
 * or standardized a response shape
 *
 * FIXME: Standardize the backend response so this insane mapping is not needed
 */
import {
  ColumnSource,
  ConfigurableHoldingColumn,
  LeverageConfig,
  ProposalSupplement,
} from '@blk/aw-bff-typescript-models';
import { Analysis, TotalPortfolioAnalysis } from '@blk/awt-aladdinportfoliobuilderserver';
import { ValueGetterParams } from 'ag-grid-community';
import { get, isNumber } from 'lodash';
import { applyMultiplierToContribution, revertMultiplierToContribution } from './AnalysisUtils';
import { search } from './JmespathUtils';

export interface PositionContributorMap {
  [positionID: string]: number | undefined;
}

export interface SleevedAnalyticSelector<T = null, P = unknown, Q = unknown> {
  // default ag-grid aggregation method for grouping
  aggFunc?: string;
  getProposalFee?: (proposalSupplementParams: Q) => PositionContributorMap;
  // the selector for security contributions. always return a map of cusip to number
  securityContributionForProposableAnalysis: (
    sleevedAnalysis: Analysis,
    args: T,
    optionalDynamicPathArgs?: string
  ) => PositionContributorMap;
  // the selector for the portfolio-level total column. if returns null, the cell in the total footer will be blank
  totalForProposableAnalysis: (sleevedAnalysis: Analysis, args: T, optionalDynamicPathArgs?: string) => number | null;
  valueGetterFactory?: (factoryParams: P) => (params: ValueGetterParams) => null | number | string;
}

export interface SleevedAnalyticValueGetterFactoryParams {
  column?: ConfigurableHoldingColumn;
  currentTopLevelGrouping?: string;
  leverage?: LeverageConfig;
  totalPortfolioAnalysis?: TotalPortfolioAnalysis;
}

export interface SleevedAnaltyicProposalSupplementParams {
  // entityFeePath points to the path inside the EntityComponent of AggregateFee for ProposalFee shape
  entityFeePath: string;
  feeName: string;
  multiplier?: number;
  proposalSupplement?: ProposalSupplement;
  /*
   * This sleeve tag is used to create a "Sleeve_tag:sleeve_tag_value: 0.32' key to assign fee from Entity in Proposal Supplement Response.
   * In SecurityAnalyticsForPortfolio, if a position have a sleeve field. Example: posInfo: { sma_manager} and the expecations is to have sleeve
   * collapsed, then position is assigned the fee as an analytic value. In SVG_ACCOUNT_HOLDINGS_TABLE, we can use aggregations methond 'FIRST' to show
   * correct fee for a sleeve retrieved from the underlying positions.
   */
  sleeveTag?: string;
  source?: ColumnSource;
}

/**
 **
 * For each analytic, define how to select it out of the AWA Analysis object for both a single position and a portfolio
 */

const risk: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis) =>
    analysis.risk
      ? analysis.risk.positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
          positionMap[contributor.positionID] = contributor.contribution;
          return positionMap;
        }, {})
      : {},
  totalForProposableAnalysis: (analysis) => (analysis.risk ? analysis.risk.totalRisk : null),
};

const riskAsPercentageOfTotal: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis) => {
    if (analysis.risk) {
      const totalRisk = analysis.risk.totalRisk;
      return analysis.risk.positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
        positionMap[contributor.positionID] = contributor.contribution / totalRisk;
        return positionMap;
      }, {});
    }
    return {};
  },
  totalForProposableAnalysis: () => 1,
};

const esgScore: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis, _, scoreName) =>
    scoreName && analysis.esg?.[scoreName]
      ? analysis.esg[scoreName].positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
          positionMap[contributor.positionID] = contributor.contribution;
          return positionMap;
        }, {})
      : {},
  totalForProposableAnalysis: (analysis, _, scoreName) =>
    scoreName ? (analysis.esg?.[scoreName]?.total ?? null) : null,
};

const esgScoreAsPercentageOfTotal: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis, _, scoreName) => {
    if (scoreName && analysis.esg?.[scoreName]) {
      const totalEsg = analysis.esg[scoreName].total || 1;
      return analysis.esg[scoreName].positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
        positionMap[contributor.positionID] = contributor.contribution / totalEsg;
        return positionMap;
      }, {});
    }
    return {};
  },
  totalForProposableAnalysis: () => 1,
};

const standaloneRisk: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis) =>
    analysis.risk
      ? analysis.risk.positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
          positionMap[contributor.positionID] = contributor.standalone;
          return positionMap;
        }, {})
      : {},
  totalForProposableAnalysis: (analysis) => analysis.risk?.totalStandaloneRisk ?? null,
};

const securityStandaloneRisk: SleevedAnalyticSelector<null, SleevedAnalyticValueGetterFactoryParams> = {
  valueGetterFactory: (factoryParams) => {
    const { totalPortfolioAnalysis, column, leverage, currentTopLevelGrouping } = factoryParams;
    return (params) => {
      const { node, colDef, data } = params;
      if (!node?.group) {
        // not a group level cell, return normal value
        return colDef.field ? get(data, colDef.field) : undefined;
      }

      /*
       * sleeve analysis calculation only makes sense at top level grouping
       * leverage is always top level grouping so implicitly we need !leverage too
       */
      const isTopLevelGrouping = !!node?.group && node.level === 0 && !leverage;
      const foundDynamicSleeveAtTopLevelGrouping = totalPortfolioAnalysis?.dynamicSleeves.find(
        (ds) => ds.tag === currentTopLevelGrouping
      );
      const currentNodeKey = node.key;
      const currentTopLevelGroupingSleeve = foundDynamicSleeveAtTopLevelGrouping?.sleeves.find(
        (sleeve) => sleeve.value === currentNodeKey
      );
      if (isTopLevelGrouping && column?.sleeveAggregateField && foundDynamicSleeveAtTopLevelGrouping) {
        if (currentTopLevelGroupingSleeve?.sleeveAnalysis.portfolio) {
          let stats = get(currentTopLevelGroupingSleeve.sleeveAnalysis.portfolio, column.sleeveAggregateField);
          // apply 100 multiplier for contribution columns, stay consistent with the logic under SecurityAnalyticsForPortfolio.ts
          if (params.colDef?.colId && isNumber(stats)) {
            stats = applyMultiplierToContribution(params.colDef.colId, stats);
            return stats;
          }
        }
      }
      return null;
    };
  },
  securityContributionForProposableAnalysis: (analysis) =>
    analysis.risk
      ? analysis.risk.positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
          positionMap[contributor.positionID] = contributor.securityStandalone;
          return positionMap;
        }, {})
      : {},
  totalForProposableAnalysis: () => null,
};

const securityCorrelation: SleevedAnalyticSelector<null, SleevedAnalyticValueGetterFactoryParams> = {
  valueGetterFactory: (factoryParams) => {
    const { totalPortfolioAnalysis, column, leverage, currentTopLevelGrouping } = factoryParams;
    return (params) => {
      const { colDef, node, data } = params;
      if (!node?.group) {
        // not a group level cell, return normal value
        return colDef.field ? get(data, colDef.field) : undefined;
      }

      /*
       * sleeve analysis calculation only makes sense at top level grouping
       * leverage is always top level grouping so implicitly we need !leverage too
       */
      const isTopLevelGrouping = !!node?.group && node.level === 0 && !leverage;
      const foundDynamicSleeveAtTopLevelGrouping = totalPortfolioAnalysis?.dynamicSleeves.find(
        (ds) => ds.tag === currentTopLevelGrouping
      );
      const currentNodeKey = node?.key;
      const currentTopLevelGroupingSleeve = foundDynamicSleeveAtTopLevelGrouping?.sleeves.find(
        (sleeve) => sleeve.value === currentNodeKey
      );

      if (
        isTopLevelGrouping &&
        column &&
        column.enableCustomGroupedCalc &&
        currentTopLevelGroupingSleeve?.sleeveAnalysis.portfolio
      ) {
        const { aggData: { risk: riskContribution, weight } = {} } = node;
        const standaloneRisk = get(
          currentTopLevelGroupingSleeve.sleeveAnalysis.portfolio,
          'risk.totalRisk' // todo: source from standaloneRiskColumn?.sleeveAggregateField
        );
        if (!!riskContribution && !!standaloneRisk && !!weight) {
          // for further enhancement, we would like to convert this hard coded formula to be configurable
          const computedSecurityCorrelation =
            revertMultiplierToContribution('risk', riskContribution) / weight / standaloneRisk;
          return computedSecurityCorrelation;
        }
      }

      return null;
    };
  },
  securityContributionForProposableAnalysis: (analysis) =>
    analysis.risk
      ? analysis.risk.positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
          positionMap[contributor.positionID] = contributor.securityCorrelation;
          return positionMap;
        }, {})
      : {},
  totalForProposableAnalysis: () => null,
};

const diversificationRisk: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis) =>
    analysis.risk
      ? analysis.risk.positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
          positionMap[contributor.positionID] = contributor.diversification;
          return positionMap;
        }, {})
      : {},
  totalForProposableAnalysis: (analysis) => analysis.risk?.totalDiversification ?? null,
};

const expectedShortfall: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis) =>
    analysis.tailRisk
      ? analysis.tailRisk.expectedShortfall.positionContributors.reduce<PositionContributorMap>(
          (positionMap, contributor) => {
            positionMap[contributor.positionID] = contributor.contribution;
            return positionMap;
          },
          {}
        )
      : {},
  totalForProposableAnalysis: (analysis) => (analysis.tailRisk ? analysis.tailRisk.expectedShortfall.total : null),
};

const expectedShortfallAsPercentageOfTotal: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis) => {
    if (analysis.tailRisk) {
      const totalRisk = analysis.tailRisk.expectedShortfall.total;
      return analysis.tailRisk.expectedShortfall.positionContributors.reduce<PositionContributorMap>(
        (positionMap, contributor) => {
          positionMap[contributor.positionID] = contributor.contribution / totalRisk;
          return positionMap;
        },
        {}
      );
    }
    return {};
  },
  totalForProposableAnalysis: () => 1,
};

const hvar: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis) =>
    analysis.tailRisk
      ? analysis.tailRisk.hvar.positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
          positionMap[contributor.positionID] = contributor.contribution;
          return positionMap;
        }, {})
      : {},
  totalForProposableAnalysis: (analysis) => (analysis.tailRisk ? analysis.tailRisk.hvar.total : null),
};

const scenarios: SleevedAnalyticSelector<string> = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis, scenarioTag: string) => {
    if (analysis.scenarios) {
      const scenario = analysis.scenarios[scenarioTag];
      return scenario
        ? scenario.positionContributors.reduce<PositionContributorMap>((positionMap, contributor) => {
            positionMap[contributor.positionID] = contributor.contribution;
            return positionMap;
          }, {})
        : {};
    }
    return {};
  },
  totalForProposableAnalysis: (analysis, scenarioTag: string) => {
    if (analysis.scenarios) {
      const scenario = analysis.scenarios[scenarioTag];
      return scenario ? scenario.total : null;
    }
    return null;
  },
};

const duration: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis) => analysis.characteristics?.duration?.contributors ?? {},
  totalForProposableAnalysis: (analysis) => analysis.characteristics?.duration?.total ?? null,
};

const twelveMonthTrailingYield: SleevedAnalyticSelector = {
  aggFunc: 'sum',
  securityContributionForProposableAnalysis: (analysis) =>
    analysis.characteristics?.twelveMonthTrailingYield?.contributors ?? {},
  totalForProposableAnalysis: (analysis) => analysis.characteristics?.twelveMonthTrailingYield?.total ?? null,
};

const proposalSupplementPositionFee: SleevedAnalyticSelector<null, unknown, SleevedAnaltyicProposalSupplementParams> = {
  aggFunc: 'count',
  getProposalFee: (proposalParams): PositionContributorMap => {
    const { proposalSupplement, feeName, sleeveTag, entityFeePath, multiplier } = proposalParams;

    // Creates a hash for proposal fees to be accessed easily
    return Object.fromEntries(
      proposalSupplement?.proposalFees?.fees
        .find((fee) => fee.name === feeName)
        ?.proposed.entityFees.map((entityFee) => {
          const {
            component: { entityId, componentType },
          } = entityFee;
          // TODO: might have to add a multiplier
          const fee: number = Number(search(entityFee, entityFeePath)) * (multiplier ?? 1);
          let key = entityId;
          if (componentType === 'SLEEVE') {
            key = `${sleeveTag}:${key}`;
          }
          return [key, fee];
        }) ?? []
    );
  },
  securityContributionForProposableAnalysis: function (): PositionContributorMap {
    return {};
  },
  totalForProposableAnalysis: function (): number | null {
    return null;
  },
};

/**
 **
 * Assemble all selectors into a collection of SLEEVED_SELECTORS
 */
export const securityAnalyticsSelectors = {
  risk,
  riskAsPercentageOfTotal,
  esgScore,
  esgScoreAsPercentageOfTotal,
  standaloneRisk,
  securityCorrelation,
  diversificationRisk,
  expectedShortfall,
  expectedShortfallAsPercentageOfTotal,
  hvar,
  scenarios,
  securityStandaloneRisk,
  duration,
  twelveMonthTrailingYield,
  proposalSupplementPositionFee,
};

export const isSecuritySelector = (selectorName: string): selectorName is keyof typeof securityAnalyticsSelectors =>
  securityAnalyticsSelectors.hasOwnProperty(selectorName);

export const getSecurityAnalyticsSelectorByName = (name: keyof typeof securityAnalyticsSelectors | string) => {
  // for dynamic col selectors (e.g. esg contributors), the selector name would follow the pattern: {selectorName}_{field}; this is required as selector names are expected to be unique when building the data
  const selectorName = name.split('_')[0];
  if (isSecuritySelector(selectorName)) {
    return securityAnalyticsSelectors[selectorName];
  }

  return securityAnalyticsSelectors.scenarios;
};
